import { useFormContext, useFormState, useWatch } from "react-hook-form";

import {
  ELEMENT_WRAPPER_MB,
  ElementWrapper,
  ErrorMessage,
  InputCheckbox,
  InputRadio,
  Label,
  LabelCheckbox,
  LabelRadio,
  RenderElement,
  createValidation,
  getErrorMessage,
} from "@up/form";

import type { TErrorMessageKey } from "@up/form";

export const TypeOfSchool = function () {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "typeOfSchool";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label required>What type of school do you attend?</Label>
      <div>
        <LabelRadio text="Public district or other government-run school">
          <InputRadio
            registerProps={register(
              KEY,
              createValidation(VALIDATION, formValues),
            )}
            value="Public district or other government-run school"
          />
        </LabelRadio>
        <LabelRadio text="Charter school">
          <InputRadio
            registerProps={register(
              KEY,
              createValidation(VALIDATION, formValues),
            )}
            value="Charter school"
          />
        </LabelRadio>
        <LabelRadio text="Private school">
          <InputRadio
            registerProps={register(
              KEY,
              createValidation(VALIDATION, formValues),
            )}
            value="Private school"
          />
        </LabelRadio>
        <LabelRadio text="Other">
          <InputRadio
            registerProps={register(
              KEY,
              createValidation(VALIDATION, formValues),
            )}
            value="Other"
          />
        </LabelRadio>
      </div>
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const HonestyPledge = function ({
  partnerName,
}: {
  partnerName: string;
}) {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "honestyPledge";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <LabelCheckbox
        Label={
          <>
            I certify that the information I have provided in this application
            is true to the best of my knowledge. I understand that {partnerName}{" "}
            may request verification of the information that I have provided.
          </>
        }
        required
      >
        <input
          type="checkbox"
          value="yes"
          {...register(KEY, createValidation(VALIDATION, formValues))}
        />
      </LabelCheckbox>
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const QualifiedBenefits = function () {
  const { control, register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  return (
    <fieldset className="kup-fieldset kup-fieldset-checkbox-list">
      <p>
        <strong>
          For which of the following do you or your parent(s)/guardian(s)
          qualify?
        </strong>
      </p>
      <p>
        <strong>
          Select all that apply. If you&apos;re not sure, please leave
          unchecked.
        </strong>
      </p>

      <br />

      <ElementWrapper>
        <LabelCheckbox
          Label={
            <>
              SNAP (Supplemental Nutrition Assistance Program); Food Stamps;
              Electronic Benefits Transfer (EBT) card; WIC; etc.
            </>
          }
        >
          <InputCheckbox registerProps={{ ...register("snap") }} value="yes" />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox Label={<>Public or subsidized housing</>}>
          <InputCheckbox
            registerProps={{ ...register("publicHousing") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox
          Label={
            <>Medicaid, CHIP, or other government health insurance benefits</>
          }
        >
          <InputCheckbox
            registerProps={{ ...register("medicaid") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox Label={<>Social security disability benefits (SSDI)</>}>
          <InputCheckbox registerProps={{ ...register("ssdi") }} value="yes" />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox Label={<>SAT/ACT Registration Fee Waiver</>}>
          <InputCheckbox
            registerProps={{ ...register("satRegistrationFeeWaiver") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox Label={<>Pell Grant</>}>
          <InputCheckbox
            registerProps={{ ...register("pellGrant") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox Label={<>Future first-generation college student</>}>
          <InputCheckbox
            registerProps={{ ...register("futureFirstGeneration") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox
          Label={
            <>Do not qualify/unsure whether we qualify for any of the above</>
          }
        >
          <InputCheckbox
            registerProps={{ ...register("unsureBenefits") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <ElementWrapper>
        <LabelCheckbox
          Label={<>Other government benefits for low-income individuals</>}
        >
          <InputCheckbox
            registerProps={{ ...register("otherGovernmentBenefits") }}
            value="yes"
          />
        </LabelCheckbox>
      </ElementWrapper>

      <RenderElement
        control={control}
        element={{
          _id: "xyz123",
          type: "textarea",
          name: "otherGovernmentBenefitsDetails",
          label: "Please specify",
          dependent: {
            dependentName: "otherGovernmentBenefits",
            condition: "equal",
            conditionValue: "yes",
          },
          validate: {
            presence: true,
          },
        }}
        errors={errors}
        formValues={formValues}
        register={register}
      />
    </fieldset>
  );
};

export const Statement = function () {
  const { control, register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  return (
    <RenderElement
      control={control}
      element={{
        _id: "statement-textarea",
        type: "textarea",
        name: "statement",
        label:
          "Please use the space below to describe any special circumstances, financial hardship, or other relevant details that may help us assess your financial need.",
      }}
      errors={errors}
      formValues={formValues}
      register={register}
      wrapperProps={{ className: "mb-8" }}
    />
  );
};

export const Goals = function () {
  const { control, register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  return (
    <RenderElement
      control={control}
      element={{
        _id: "goals-textarea",
        type: "textarea",
        name: "goals",
        label:
          "We have limited scholarship funds available. Please use the space below to tell us more about how a scholarship award would help you to achieve your goals.",
      }}
      errors={errors}
      formValues={formValues}
      register={register}
      wrapperProps={{ className: "mb-8" }}
    />
  );
};

export const PrivateSchoolAssistance = function () {
  const { control, register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  return (
    <RenderElement
      control={control}
      element={{
        _id: "private-school-assistance-select",
        type: "select",
        name: "privateSchoolAssistance",
        label: "Do you receive need-based scholarships to attend your school?",
        options: ["yes", "no", "I don't know"],
        dependent: {
          dependentName: "typeOfSchool",
          condition: "equal",
          conditionValue: "Private school",
        },
        validate: {
          presence: true,
        },
      }}
      errors={errors}
      formValues={formValues}
      register={register}
      wrapperProps={{ className: "mb-8" }}
    />
  );
};
